import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { formatCurrency, type CurrencyFormatOptions } from '../u2000Core/tools/U2000CurrencyUtils';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { U3146ContextProvider } from './U3146Context';
import { doInitLU, luHandleAppiError, parseU3146InitLU, type U3146InitLU } from './U3146Service';

export const NS = 'u3146';

export const U3146ROLES = {
  READ: 'U3146:READ',
  CLOSE: 'U3146:CLOSE',
  EDIT: 'U3146:EDIT',
} as const;
export type U3146ROLES = (typeof U3146ROLES)[keyof typeof U3146ROLES];

export const U3146_CURRENCY_FORMAT: CurrencyFormatOptions = {
  currencySymbol: '$',
  symbolPosition: 'left',
  decimalSeparator: '.',
  thousandSeparator: ' ',
  decimalDigits: 2,
  allowNegative: true,
};

export function u3146FormatCurrency(value: unknown): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return formatCurrency(n, U3146_CURRENCY_FORMAT);
}

const defaultInitLU: U3146InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 25,
  personalization: {},
};

const U3146: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3146InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3146'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3146InitLU>({
    doInitLUFn: async () => await doInitLU(u2000AxiosAuthenticated),
    onSuccess: (initLU: U3146InitLU) => {
      setInitLUData(parseU3146InitLU(initLU));
      setInitLUReady(true);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3146ContextProvider initLU={initLUData}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    </U3146ContextProvider>
  );
};

export default U3146;
